import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  LocationSummaryColumnKey,
  SortDirection,
} from '../constants/locationSummaryColumns';
import { LocationRecord, WeekRecord } from '../constants/locationSummaryMockData';
import { buildFlatRows } from '../util/hierarchyHelpers';
import {
  setLocationSummaryLoading,
  setLocationSummaryData,
  setLocationSummaryError,
} from '../../../store/slices/perfectTruckLocationSummarySlice';
import {
  fetchLocationDetails,
  LocationDetailsParams,
  LocationDetailRecord,
} from '../pages/PerfectTruckAnalytics/perfectTruckKpiApi';
import { RootState } from '../../../store';
import { AppDispatch } from '../../../store';

const DEFAULT_SORT_KEY: LocationSummaryColumnKey = 'region';
const DEFAULT_SORT_DIRECTION: SortDirection = 'asc';
const PAGE_SIZE = 10;

export interface LocationSummaryCardItem {
  rowKey: string;
  expanded: boolean;
  region: string;
  market: string;
  location: LocationRecord;
}

// ─── Map API record to internal LocationRecord ────────────────────────────────

const toLocationRecord = (record: LocationDetailRecord, index: number): LocationRecord => ({
  id: `${record.location}-${record.region}-${record.market}-${index}`,
  name: record.location,
  created: record.created,
  target: record.target,
  ptExecution: record.ptExecution,
  approved: record.approved,
  pending: record.pending,
  declined: record.declined,
  weeks: record.timeframeBreakdown.map<WeekRecord>((tb) => ({
    id: `${tb.label}-${tb.subLabel ?? ''}`,
    // Display period first (e.g. P1W1) — backend provides `label` (week) and
    // `subLabel` (period). Previously we rendered "W1 P1"; swap order.
    weekLabel: tb.subLabel ? `${tb.subLabel}${tb.label}` : tb.label,
    created: tb.created,
    target: tb.target,
    ptExecution: tb.execution,
    approved: tb.approved,
    pending: tb.pending,
    declined: tb.declined,
  })),
});

// ─── Hook ─────────────────────────────────────────────────────────────────────

export const useLocationSummaryTable = (filterParams: LocationDetailsParams = {}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { data, total, page, totalPages, loading, error } = useSelector(
    (state: RootState) => state.perfectTruckLocationSummary,
  );
  const adminLocations = useSelector((state: RootState) => state.adminLocations.locations);

  const [sortKey, setSortKey] = useState<LocationSummaryColumnKey>(DEFAULT_SORT_KEY);
  const [sortDirection, setSortDirection] = useState<SortDirection>(DEFAULT_SORT_DIRECTION);
  const [currentPage, setCurrentPage] = useState(0);
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});

  // ─── Fetch from API ──────────────────────────────────────────────────────
  const loadData = useCallback(
    async (pageNum: number, sortK: LocationSummaryColumnKey, sortDir: SortDirection) => {
      dispatch(setLocationSummaryLoading());
      try {
        const result = await fetchLocationDetails({
          ...filterParams,
          page: pageNum,
          size: PAGE_SIZE,
          sortBy: sortK,
          sortDirection: sortDir,
        });
        dispatch(setLocationSummaryData(result));
      } catch (e: any) {
        dispatch(setLocationSummaryError(e?.message ?? 'Failed to load location summary'));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch, JSON.stringify(filterParams)],
  );

  // Fetch when page, sort, or filters change
  useEffect(() => {
    loadData(currentPage, sortKey, sortDirection);
  }, [currentPage, sortKey, sortDirection, loadData]);

  // Reset to page 0 when filter params change
  useEffect(() => {
    setCurrentPage(0);
    setExpandedRows({});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(filterParams)]);

  // ─── Map API data to table rows ──────────────────────────────────────────
  const flattenedLocations = useMemo(() => {
    // If server returned data, map normally
    if (data && data.length > 0) {
      return data.map((record, index) => ({
        region: record.region,
        market: record.market,
        location: toLocationRecord(record, index),
      }));
    }

    // Server returned no data. If the user filtered by region/market/location,
    // synthesize rows from the cached admin locations so the UI still shows
    // the expected location entries (with zero / N/A metrics) instead of
    // 'No rows available'. This keeps UX consistent when the backend returns
    // an empty set for a known location.
    const hasGeoFilter = Boolean(filterParams.region || filterParams.market || filterParams.location);
    if (!hasGeoFilter || !adminLocations || adminLocations.length === 0) return [];

    // Resolve matching admin locations
    const matched = adminLocations.filter((loc: any) => {
      const matchesRegion = !filterParams.region || String(loc.region).toLowerCase().trim() === String(filterParams.region).toLowerCase().trim();
      const matchesMarket = !filterParams.market || String(loc.market).toLowerCase().trim() === String(filterParams.market).toLowerCase().trim();
      const matchesLocation = !filterParams.location || String(loc.locName).toLowerCase().trim() === String(filterParams.location).toLowerCase().trim();
      return matchesRegion && matchesMarket && matchesLocation;
    });

    // Build zero-valued LocationRecord objects for each matched admin loc
    return matched.map((loc: any, idx: number) => ({
      region: loc.region ?? '',
      market: loc.market ?? '',
      location: {
        id: String(loc.locId ?? `${loc.locName}-${idx}`),
        name: loc.locName ?? loc.name ?? 'Unknown',
        created: 0,
        target: 0,
        ptExecution: 0,
        approved: 0,
        pending: 0,
        declined: 0,
        weeks: [],
      },
    }));
  }, [data, filterParams, adminLocations]);

  const rows = useMemo(
    () => buildFlatRows(flattenedLocations, expandedRows),
    [flattenedLocations, expandedRows],
  );

  const locationCards = useMemo<LocationSummaryCardItem[]>(
    () =>
      flattenedLocations.map(({ region, market, location }) => ({
        rowKey: `location:${location.id}`,
        expanded: Boolean(expandedRows[`location:${location.id}`]),
        region,
        market,
        location,
      })),
    [expandedRows, flattenedLocations],
  );

  // ─── Sorting ─────────────────────────────────────────────────────────────
  const onSort = (key: LocationSummaryColumnKey) => {
    if (sortKey === key) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortKey(key);
    setSortDirection('asc');
    setCurrentPage(0);
  };

  // ─── Expand/Collapse ─────────────────────────────────────────────────────
  const toggleExpanded = (rowKey: string) => {
    setExpandedRows((prev) => ({ ...prev, [rowKey]: !prev[rowKey] }));
  };

  // ─── Pagination ───────────────────────────────────────────────────────────
  const goToPage = (pageNum: number) => {
    if (pageNum < 0 || pageNum >= totalPages) return;
    setCurrentPage(pageNum);
    setExpandedRows({});
  };

  return {
    rows,
    locationCards,
    sortKey,
    sortDirection,
    onSort,
    toggleExpanded,
    loading,
    error,
    currentPage,
    totalPages,
    total,
    pageSize: PAGE_SIZE,
    goToPage,
  };
};
